// Optional Google Places address suggestions over a plain text input. If the
// Maps script can't load (or the key isn't allowed on this site), the field
// just stays a plain text input, which works fine on its own.

declare const google: {
  maps: {
    importLibrary(name: string): Promise<unknown>;
    places: { PlaceAutocompleteElement: new (options: Record<string, unknown>) => HTMLElement };
  };
};

const GOOGLE_PLACES_API_KEY = 'AIzaSyAOeniUPxaf0oPn3FhlTUCKp7EvrRc5yX4';
let ready: Promise<boolean> | null = null;

function loadPlaces(): Promise<boolean> {
  if (!ready) {
    ready = new Promise((resolve) => {
      const w = window as unknown as Record<string, unknown>;
      w.__qeInitPlaces = async () => {
        try {
          await google.maps.importLibrary('places');
          resolve(true);
        } catch {
          resolve(false);
        }
      };
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&callback=__qeInitPlaces`;
      script.async = true;
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });
  }
  return ready;
}

// Resolves once the input's dialog (or the input itself, outside one) is on
// screen, so the Maps script (~180 KB) is only fetched by visitors who open
// the form, and is ready by the time they reach the address field.
function whenShown(input: HTMLInputElement): Promise<void> {
  if (!('IntersectionObserver' in window)) return Promise.resolve();
  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(input.closest('.dialog') || input);
  });
}

/**
 * Puts a Places suggestion widget over the input (hiding the input, which
 * keeps the value). An input that already has a value is left plain and
 * editable, since the widget can't be pre-filled; clearing it brings the
 * widget back. restrictToGb for property addresses; company addresses can
 * be anywhere. Waits for the input to be shown before the Maps script is
 * first fetched.
 */
export async function attachPlaces(input: HTMLInputElement, onChange: (value: string) => void, restrictToGb: boolean): Promise<void> {
  if (!ready) await whenShown(input);
  if (!(await loadPlaces())) return;
  try {
    const existing = input.nextElementSibling?.tagName === 'GMP-PLACE-AUTOCOMPLETE' ? input.nextElementSibling : null;
    if (input.value.trim() !== '') {
      existing?.remove();
      input.style.display = '';
      if (!input.dataset.placesRetry) {
        input.dataset.placesRetry = '1';
        input.addEventListener('input', () => { if (input.value.trim() === '') void attachPlaces(input, onChange, restrictToGb); });
      }
      return;
    }
    if (existing) return;
    const widget = new google.maps.places.PlaceAutocompleteElement({
      ...(restrictToGb ? { includedRegionCodes: ['gb'] } : {}),
      includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'postal_code'],
    });
    widget.style.width = '100%';
    widget.style.minHeight = '40px';
    input.style.display = 'none';
    input.insertAdjacentElement('afterend', widget);
    widget.addEventListener('gmp-select', async (e: Event) => {
      const { placePrediction } = e as unknown as { placePrediction: { toPlace(): { fetchFields(o: object): Promise<void>; formattedAddress?: string } } };
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress'] });
      input.value = place.formattedAddress || '';
      onChange(input.value);
    });
    // Typing without picking a suggestion still counts: the widget's own
    // input event comes out through its shadow boundary.
    widget.addEventListener('input', (e) => {
      const inner = e.composedPath()[0] as HTMLInputElement | undefined;
      if (inner && typeof inner.value === 'string') {
        input.value = inner.value;
        onChange(inner.value);
      }
    });
  } catch (err) {
    console.warn('Address suggestions unavailable; the field stays plain text.', err);
  }
}

/** Sets an address field's value from code (e.g. a company's registered address) and refreshes the widget. */
export function setAddressValue(input: HTMLInputElement, value: string, onChange: (value: string) => void, restrictToGb: boolean): void {
  input.value = value || '';
  void attachPlaces(input, onChange, restrictToGb);
}
